import { use, useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { GET_BANK_DETAILS_FOR_IBAN, UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { AuthManagerContext, AuthStatus } from "../../contexts/AuthManager";
import TextField from "../../components/TextField";
import VStack from "../../ui_atoms/VStack";
import { isIbanValid, isPostFinanceIban } from "../../iban";
import { isBicValid } from "../../bic";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";
import Switch from "../../ui_atoms/Switch";
import Text from "../../components/Text";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import HStack from "../../ui_atoms/HStack";
import { StyleSheet } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import OptionsField from "../../components/OptionsField";
import { BodyMediumText } from "../../components/FormattedText";
import Callout from "../../ui_atoms/Callout";

type FormData = {
    iban: string;
    bic: string;
    beneficiary: string;
};

type BankAccountEditorScreenProps = StackScreenProps<RootStackParamList, "BankAccountEdit">;

const BankAccountEditorScreen = ({ navigation, route }: BankAccountEditorScreenProps) => {
    const { profileId } = (route.params || {}) as RootStackParamList["BankAccountEditModal"];
    const { user, status, refreshUser } = use(AuthManagerContext);
    const [updateWorkerProfile, { error: saveError, loading: saving, reset }] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    const [fetchBankDetails, { error: bankDetailsError, loading: resolvingBic }] =
        useLazyQuery(GET_BANK_DETAILS_FOR_IBAN);

    useApolloErrorSnackbar(saveError, reset);
    useApolloErrorSnackbar(bankDetailsError);

    const loading = status == AuthStatus.Loading;
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const workerProfile = user?.workerProfile;

    // The BIC is derived from the IBAN by the backend. The field stays locked until the backend
    // answers that it could not find one, and only then does it accept a manual input.
    const [isBICDisabled, setIsBICDisabled] = useState(true);
    const [bankName, setBankName] = useState("");
    // The transfer goes to the account holder unless a different beneficiary was recorded, in
    // which case the switch has to come up already on. Without a known holder the field is opened,
    // it is required and nobody else can fill it.
    const accountHolder = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    const storedBeneficiary = workerProfile?.bankAccount?.beneficiary || "";
    const [isBeneficiaryDisabled, setIsBeneficiaryDisabled] = useState(
        () => !!accountHolder && (!storedBeneficiary || storedBeneficiary === accountHolder),
    );

    // BAS-2971: a salary cannot be paid into a REVOLUT account. Its Swiss IBANs cannot be told
    // apart from PostFinance ones, so the worker is asked, and until they answer no the account
    // cannot be saved. The answer belongs to the IBAN it was given for: it is cleared on any edit.
    const [isRevolut, setIsRevolut] = useState<boolean | undefined>(undefined);

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            if (
                data.iban !== workerProfile?.bankAccount?.iban &&
                isPostFinanceIban(data.iban) &&
                isRevolut !== false
            ) {
                return;
            }
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            bankAccount: {
                                id: workerProfile?.bankAccount?.id || null,
                                iban: data.iban,
                                bic: data.bic,
                                beneficiary: data.beneficiary,
                            },
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.(); // do not await to avoid visual glitch
            } finally {
                // this is handled by saveError in useMutation
            }
        },
        [
            updateWorkerProfile,
            workerProfile?.bankAccount?.id,
            workerProfile?.bankAccount?.iban,
            navigation,
            refreshUser,
            isRevolut,
        ],
    );

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
        getValues,
        setValue,
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            iban: workerProfile?.bankAccount?.iban || "",
            bic: workerProfile?.bankAccount?.bic || "",
            beneficiary: storedBeneficiary || accountHolder,
        },
    });

    const { chainInputFocus, getInputRef } = useChainInputFocus();

    // Only an IBAN the worker enters is questioned: the stored one went through this already.
    const iban = useWatch({ control, name: "iban" });
    const askIsRevolut =
        iban !== workerProfile?.bankAccount?.iban && isIbanValid(iban) && isPostFinanceIban(iban);
    const revolutBlocked = askIsRevolut && isRevolut !== false;

    const disabled = loading || saving;
    const bicDisabled = isBICDisabled || resolvingBic || disabled;
    const beneficiaryDisabled = isBeneficiaryDisabled || disabled;

    useCloseEditor(isDirty && !saving);

    const resolvedIban = useRef<string | null>(null);

    const applyBankDetails = useCallback(
        async (iban: string, { markDirty }: { markDirty: boolean }) => {
            if (!isIbanValid(iban) || resolvedIban.current === iban) {
                return;
            }
            // Nothing is unlocked on a failed call: only the backend telling us it found no BIC
            // opens the field. The snackbar tells the user the lookup did not go through.
            let data;
            try {
                const result = await fetchBankDetails({ variables: { iban } });
                if (result.error) {
                    return;
                }
                data = result.data;
            } catch {
                return;
            }
            if (getValues("iban") !== iban) {
                // The IBAN changed while the query was in flight: a later call owns the field.
                return;
            }
            resolvedIban.current = iban;
            const details = data?.getBankDetailsForIban;
            setBankName(details?.bankName ?? "");
            if (details) {
                setValue("bic", details.bic, { shouldValidate: true, shouldDirty: markDirty });
            } else if (markDirty) {
                // The IBAN changed and no BIC matches it anymore: the one on screen was derived
                // from the previous IBAN. On mount a stored BIC is kept, it is all the user has.
                setValue("bic", "", { shouldDirty: true });
            }
            setIsBICDisabled(!!details);
        },
        [fetchBankDetails, getValues, setValue],
    );

    // A stored IBAN is resolved on mount: it brings the bank name, and it unlocks the field when
    // no BIC can be derived, which an account recorded before the BIC was collected needs.
    const storedIban = workerProfile?.bankAccount?.iban;
    useEffect(() => {
        if (storedIban) {
            void applyBankDetails(storedIban, { markDirty: false });
        }
    }, [storedIban, applyBankDetails]);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty && !revolutBlocked,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                <Controller
                    control={control}
                    name="iban"
                    rules={{
                        required: true,
                        validate: (value) => {
                            if (!isIbanValid(value)) {
                                return i18n.t("worker_profile.errors.iban_number");
                            }
                            return true;
                        },
                    }}
                    render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                    }) => (
                        <TextField
                            {...chainInputFocus(name, { disabled })}
                            // BAS-2931: the bar closes the keyboard whatever the chain's return key does.
                            withIosDoneButton
                            autoCapitalize="characters"
                            error={error?.message}
                            value={value}
                            label={i18n.t("worker_profile.bank_iban")}
                            onChangeText={(text) => {
                                onChange(text);
                                setIsRevolut(undefined);
                            }}
                            onBlur={() => {
                                onBlur();
                                void applyBankDetails(value, { markDirty: true });
                            }}
                            disabled={disabled}
                        />
                    )}
                />
                {askIsRevolut && (
                    <BodyMediumText>{i18n.t("worker_profile.is_revolut_account")}</BodyMediumText>
                )}
                {askIsRevolut && (
                    <OptionsField<boolean | undefined>
                        data={[
                            { label: i18n.t("yes"), value: true },
                            { label: i18n.t("no"), value: false },
                        ]}
                        value={isRevolut}
                        onSelect={setIsRevolut}
                        disabled={disabled}
                        shrinked
                    />
                )}
                {askIsRevolut && isRevolut && (
                    <Callout
                        mode="error"
                        markdown={i18n.t("worker_profile.revolut_account_forbidden")}
                        style={styles.revolutForbidden}
                    />
                )}
                <Controller
                    control={control}
                    name="bic"
                    rules={{
                        required: true,
                        validate: (value) => {
                            if (!isBicValid(value)) {
                                return i18n.t("worker_profile.errors.bic_number");
                            }
                            return true;
                        },
                    }}
                    render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                    }) => (
                        <TextField
                            {...chainInputFocus(name, { disabled: bicDisabled })}
                            withIosDoneButton
                            autoCapitalize="characters"
                            error={error?.message}
                            value={value}
                            label={i18n.t("worker_profile.bank_bic")}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            disabled={bicDisabled}
                            right={resolvingBic ? <Loading size="small" /> : undefined}
                        />
                    )}
                />
                {!!bankName && (
                    <HStack gap={0.25} alignment="center" style={styles.bankName}>
                        <Icon name="check-circle-heavy" size={16} color={colors.success} />
                        <Text style={styles.bankNameText}>{bankName}</Text>
                    </HStack>
                )}
                <Switch
                    value={!isBeneficiaryDisabled}
                    label={i18n.t("worker_profile.custom_beneficiary")}
                    onChange={(value) => {
                        setIsBeneficiaryDisabled(!value);
                        if (value) {
                            // The input is still disabled in this render, focus it once enabled.
                            setTimeout(() => getInputRef("beneficiary")?.focus(), 0);
                        } else {
                            // Switching back hands the transfer to the account holder: the field
                            // is what gets saved, it cannot keep contradicting the switch.
                            setValue("beneficiary", accountHolder, {
                                shouldValidate: true,
                                shouldDirty: true,
                            });
                        }
                    }}
                />
                <Controller
                    control={control}
                    name="beneficiary"
                    rules={{ required: true }}
                    render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                    }) => (
                        <TextField
                            {...chainInputFocus(name, { disabled: beneficiaryDisabled })}
                            withIosDoneButton
                            selectTextOnFocus
                            // ISO 20022 caps a SEPA beneficiary name at 70 characters.
                            maxLength={70}
                            error={error?.message}
                            value={value}
                            label={i18n.t("worker_profile.bank_beneficiary")}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            disabled={beneficiaryDisabled}
                        />
                    )}
                />
            </VStack>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return StyleSheet.create({
        bankName: {
            paddingHorizontal: 16,
        },
        bankNameText: {
            ...fonts.bold,
            color: colors.onSurfaceVariant,
        },
        revolutForbidden: {
            borderWidth: 1,
            borderColor: colors.error,
        },
    });
};

export default BankAccountEditorScreen;
